// Adaptador WIRE → VISTA de la sesión del wizard.
//
// El silo (`retail-wizard-business`) devuelve `RetailWizard.WizardSession`: una fila PLANA de la
// saga (`kycStatus`, `kycModality`, `fiadoPhoneStatus`, `currentStep: 'S02'`, `paymentMethod:
// 'CASH_SALE'`…). Las pantallas del wizard consumen otro modelo, agrupado por paso
// (`kyc.status`, `phoneStatus`, `currentStep: '02'`, `paymentMethodKind: 'CASH_OR_CARD'`…).
//
// Sin esta traducción el client devolvía la fila cruda tipada como el modelo de vista — TypeScript
// no lo veía (el genérico de `apiFetch` es una aserción, no una validación) y la pantalla 02
// reventaba en la primera línea (`session.kyc.status` sobre un `kyc` inexistente). Traducir acá
// deja las pantallas intactas y el contrato del silo intacto.
//
// Regla: acá SOLO se traduce. Un dato que el wire no trae se deja ausente — jamás se inventa.
import type {
    AccountOpeningCancelReason,
    AccountOutcome,
    AccountStatus,
    AccountStatusResult,
    ActivationChecklist,
    ActiveWizardSession,
    AppOnboardingState,
    AppOnboardingStatus,
    AppOnboardingStepState,
    BlockedCategory,
    DeliveryState,
    DeviceEnrollment,
    DeviceEnrollmentFailureCode,
    DeviceEnrollmentFailureReason,
    DeviceEnrollmentResult,
    CreditPromotionBonusState,
    CreditPromotionBonusStatus,
    EmailKind,
    EmailState,
    SessionEmail,
    PromotionBonusOutcome,
    PromotionBonusStatus,
    DeviceEnrollmentStatus,
    FinancingPreview,
    KycAttemptProgress,
    KycFailure,
    KycProgress,
    KycState,
    KycStatus,
    KycSubChecks,
    LineIdentifiers,
    LinePaymentType,
    LineProduct,
    LinePromotionFields,
    LineStatus,
    MoneyAtStakeKind,
    MonthlyIncome,
    Occupation,
    PaymentMethod,
    ProofAddressStatus,
    ProofOfAddressResult,
    RegisteredPayment,
    PhoneStatus,
    ProductCategory,
    ProspectResume,
    ReservedComponent,
    ReservedLine,
    ResumeReason,
    ResumeTarget,
    SelectedProduct,
    TrackingMode,
    WelcomeState,
    WizardBlocked,
    WizardSession,
    WizardSessionState,
    WizardStepId,
    WizardStepSlug,
} from '@/domains/sales/wizard/types';
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { DeviceProvisioningTypeEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceProvisioningTypeEnum';
import { SystemCancelReasonEnum } from '@fiado/type-kit/bin/retailWizard/enums/SystemCancelReasonEnum';
import { ProspectResumeModeEnum } from '@fiado/type-kit/bin/retailWizard/enums/ProspectResumeModeEnum';
import { MoneyAtStakeKindEnum } from '@fiado/type-kit/bin/retailWizard/enums/MoneyAtStakeKindEnum';
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import type { ProspectResumeResponse } from '@fiado/type-kit/bin/retailWizard/dtos/responses/ProspectResumeResponse';
import type { components } from './retail-wizard.gen';
import { MONTHLY_INCOME_OPTIONS } from '@/domains/sales/wizard/profileCatalog';
import { toPaymentLines } from '@/shared/lib/format/salePaymentLines';
import {
    KYC_FAILURE_CODES,
    PROOF_ADDRESS_STATUSES,
    RESUME_REASONS,
    RESUME_SLUGS,
} from '@/domains/sales/wizard/types';

/**
 * Fila de la saga tal cual la publica el silo (espejo de `RetailWizard.WizardSession` del
 * `@fiado/type-kit`). Se declara acá porque los tipos generados del wizard todavía no se sincronizan
 * (TD-F2-WIZ-001); cuando se habiliten, este `interface` se reemplaza por el import.
 */
/**
 * Espejo de `RetailWizard.WizardProduct`: el snapshot comercial que el silo congela al reservar. En
 * sesiones con líneas se arma desde la línea y omite lo que no tiene (IMEI, capacidad, color, gama).
 */
export interface WizardProductWire {
    sku: string;
    imei?: string | null;
    cashPriceCents: number;
    /** Ausentes en kits y servicios dados de alta sin marca ni modelo. */
    brand?: string | null;
    model?: string | null;
    capacity?: string | null;
    color?: string | null;
    imageUrl?: string | null;
    tier?: string | null;
    eligibleFinanciers: string[];
    lineId?: string | null;
}

/** Espejo de `RetailWizard.WizardLineProduct`. */
export interface LineProductWire {
    /** Ausentes en kits y servicios dados de alta sin marca ni modelo. */
    brand?: string | null;
    model?: string | null;
    description?: string | null;
    capacity?: string | null;
    color?: string | null;
    imageUrl?: string | null;
    tier?: string | null;
    category: string;
    trackingMode: string;
    itemType?: components['schemas']['WizardLineProductFields']['itemType'] | null;
}

/** Espejo de `RetailWizard.WizardReservedLine`. */
export interface ReservedLineWire {
    lineId: string;
    sku: string;
    qty: number;
    status: string;
    expiresAt: string;
    basePriceCents: number | null;
    eligibleFinanciers?: string[] | null;
    financeable?: boolean | null;
    paymentType: string | null;
    /** Parte financiada congelada al cotizar; ausente con la regla vieja. */
    financedCents?: number | null;
    /** Promo heredada de la ancla anterior que Promociones aún no revalida. */
    promotionRevalidationPending?: boolean | null;
    role?: string | null;
    promotionId?: string | null;
    promotionVersion?: number | null;
    linkedLineId?: string | null;
    discountCode?: string | null;
    discountCents?: number | null;
    originalPriceCents?: number | null;
    /** Descuento de cada promo en la línea; en líneas viejas se deriva de `promotionId`. */
    promotionDiscounts?:
        | { promotionId: string; promotionVersion: number; discountCents: number }[]
        | null;
    codeDiscountCents?: number | null;
    sellItemCost: number | null;
    mdmEnabled?: boolean | null;
    mdmProvider?: string | null;
    components?: { sku: string; qty: number; category: string; trackingMode: string }[] | null;
    identifiers?: { sku: string; identifiers: string[] }[] | null;
    product?: LineProductWire | null;
}

export interface WizardSessionWire {
    sessionId: string;
    retailerId: string;
    storeId: string;
    sellerRetailUserId: string;
    phone: string;
    retailCustomerId: string | null;
    /** 'S01'…'S11'. */
    currentStep: string;
    /** ACTIVE | PAUSED_EXTERNAL_REFERRAL | PAUSED_NO_TOPUP | COMPLETED | EXPIRED | CANCELLED. */
    state: string;
    /** Motivo de cancelación; el sistema escribe `SystemCancelReasonEnum`. */
    cancelReason?: string | null;
    otpVerified: boolean;
    otpSkipped: boolean;
    /** Hay una reanudación esperando su OTP. Opcional: ausente = ninguna en curso. */
    resumeOtpRequired?: boolean | null;
    /** Primer hueco del onboarding (`{ step: 'S07', reason: 'ACCOUNT_READY' }`), calculado por el silo. */
    resumeTarget?: { step: string; reason: string } | null;
    /** Pantalla desde la que el vendedor guardó y salió (`build-sale`, `quote`…). */
    resumeSlug?: string | null;
    /** Venta solo de contado (WizardSessionCashOnlyFields). Ausente = venta completa. */
    cashOnly?: components['schemas']['WizardSessionCashOnlyFields']['cashOnly'] | null;
    /** Apertura de cuenta sin venta (WizardSessionAccountOpeningFields). Ausente = venta. */
    accountOpening?:
        | components['schemas']['WizardSessionAccountOpeningFields']['accountOpening']
        | null;
    /** Correo del paso 01 (WizardSessionEmailFields). Ausentes en sesiones anteriores al correo. */
    emailState?: components['schemas']['WizardSessionEmailFields']['emailState'] | null;
    emailType?: components['schemas']['WizardSessionEmailFields']['emailType'] | null;
    emailMasked?: string | null;
    email?: string | null;
    consents: { acceptedAt: string } | null;
    /** PENDING | VERIFIED | FAILED | MANUAL | REVIEW (RetailKycStatusEnum). */
    kycStatus: string | null;
    /** CUSTOMER_LINK | STORE_DEVICE | MANUAL. */
    kycModality: string | null;
    /**
     * FACEMATCH | FULL_KYC — la ruta que resolvió la bifurcación del paso 01d.
     * Opcional: los silos que todavía no la publican mandan la sesión sin el campo.
     */
    customerRoute?: string | null;
    occupation: string | null;
    occupationOther: string | null;
    /** Opcional: las sesiones abiertas antes de que el paso 02 pidiera el ingreso vuelven sin él. */
    monthlyIncome?: string | null;
    /** SIGN_UP | PENDING_PASSWORD_CREATION | ACTIVE | USER_TEMP_BLOCKED. */
    fiadoPhoneStatus: string | null;
    accountStatus: string | null;
    /**
     * La venta detenida esperando la cuenta. Opcional: los silos anteriores a este contrato mandan
     * la sesión sin el campo. `code` y `detail` viajan en el cable pero NO se mapean — ver `toBlocked`.
     */
    blocked?: {
        reason: string;
        category: string | null;
        code?: string | null;
        detail?: string | null;
        atStep?: string;
        since: string;
    } | null;
    /**
     * La tarjeta física PCF ya se asignó y entregó (paso 09). Opcional: las sesiones abiertas antes
     * de que el campo existiera vuelven sin él, y ausente NO es lo mismo que entregada.
     */
    pcfProvisioned?: boolean | null;
    /**
     * El comprador ya trae tarjeta física PCF vigente en MX (señal resuelta en el paso 01d, junto a
     * la de cuenta). Opcional: los silos que todavía no la publican mandan la sesión sin el campo.
     */
    hasPhysicalMxCard?: boolean | null;
    reservedSku: string | null;
    reservedImei: string | null;
    /**
     * Ficha comercial del equipo reservado, congelada por el silo en el S03. Ausente (`undefined`)
     * en sesiones creadas antes de que el campo existiera — por eso el tipo lo contempla.
     */
    product?: WizardProductWire | null;
    reservationExpiresAt: string | null;
    /** Líneas de la venta. Ausente en las sesiones anteriores a las líneas. */
    reservedLines?: ReservedLineWire[] | null;
    financedLineId?: string | null;
    /** Promo de venta; los dos en `null` = «Ninguna». Espejo de `RetailWizard.WizardSession`. */
    salePromotionId?: string | null;
    salePromotionVersion?: number | null;
    /** Todas las promos de venta fijadas; en sesiones viejas falta y manda `salePromotionId`. */
    salePromotions?: { promotionId: string; promotionVersion: number }[] | null;
    /** Reparto provisional antes de cotizar; solo con el reparto prendido. */
    financingPreview?: components['schemas']['WizardFinancingPreview'] | null;
    /** SUREKEEP | CASH_SALE | EXTERNAL. */
    paymentMethod: string | null;
    externalProvider: string | null;
    /** Contexto de crédito (pista SOFOM): punteros al dominio loan-*. Null en contado/externa. */
    loan?: {
        borrowerId: string;
        sciEvaluationId?: string;
        level?: string;
        planId?: string;
        creditId?: string;
    } | null;
    folio: string | null;
    payment: {
        method: string | null;
        amountCents: number;
        detail?: string;
        payments?: components['schemas']['SalePayment'][] | null;
        paidAt: string;
    } | null;
    welcome: {
        addressConfirmed: boolean;
        benefCaptured: boolean;
        annexSigned: boolean;
        topupApplied: boolean;
        accountLevel?: 1 | 2;
    } | null;
    /** Bono de la promo de crédito; opcional porque los silos anteriores no lo mandan. */
    creditPromotionBonus?: {
        promotionId?: string | null;
        amountCents?: number | null;
        status?: string | null;
        pendingSince?: string | null;
    } | null;
    saleId: string | null;
    /** Marca de la saga: todas las piezas rastreadas quedaron HELD de la sesión. */
    piecesHeldAt?: components['schemas']['WizardSessionPieces']['piecesHeldAt'];
    updatedAt: number | string;
}

// El allowlist tiene que cubrir los cinco estados del silo: el que falte cae al default `ACTIVE` y
// deja muertas las pantallas que lo esperan, sin que nada avise.
const WIZARD_STATES = new Set<WizardSessionState>([
    'ACTIVE',
    'CANCELLED',
    'COMPLETED',
    'PAUSED_CURP_CONFLICT',
    'PAUSED_EXTERNAL_REFERRAL',
    'PAUSED_NO_TOPUP',
]);

/** `EXPIRED` no existe en el modelo de vista: para el vendedor una sesión vencida está cerrada. */
function toState(state: string): WizardSessionState {
    if (state === 'EXPIRED') return 'CANCELLED';
    return WIZARD_STATES.has(state as WizardSessionState)
        ? (state as WizardSessionState)
        : 'ACTIVE';
}

/** 'S02' → '02'. El silo numera los pasos con prefijo; la UI y las rutas, sin él. */
function toStepId(step: string): WizardStepId {
    const id = step.replace(/^S/, '');
    return (/^(0[1-9]|1[01])$/.test(id) ? id : '01') as WizardStepId;
}

/**
 * `FiadoPhoneStatusEnum` → el binario que usa la UI. Solo `SIGN_UP` significa "teléfono nuevo";
 * cualquier otro estado implica que el gate encontró un cliente Fiado previo.
 */
function toPhoneStatus(status: string | null): PhoneStatus {
    return status && status !== 'SIGN_UP' ? 'RECOGNIZED' : 'NEW';
}

/**
 * `RetailKycStatusEnum` (+ la modalidad ya elegida) → el estado que renderiza la pantalla 02.
 *
 * El silo no tiene `WAITING`: mientras la verificación está en vuelo la fila sigue en `PENDING`. La
 * diferencia entre "todavía no arrancó" y "está en curso" es que YA hay modalidad persistida —
 * `prepare` la escribe antes de entregar la config del SDK. `PLD` tampoco existe en el silo (viaja
 * dentro de `FAILED`), así que nunca se emite.
 */
function toKycStatus(status: string | null, modality: string | null): KycStatus {
    switch (status) {
        case 'VERIFIED':
            return 'VERIFIED';
        case 'FAILED':
            return 'FAILED';
        case 'MANUAL':
            return 'MANUAL';
        case 'REVIEW':
            return 'REVIEW';
        default:
            return modality && modality !== 'MANUAL' ? 'WAITING' : 'READY';
    }
}

function toKyc(wire: WizardSessionWire): KycState {
    const modality =
        wire.kycModality === 'STORE_DEVICE' || wire.kycModality === 'CUSTOMER_LINK'
            ? wire.kycModality
            : undefined;
    return {
        status: toKycStatus(wire.kycStatus, wire.kycModality),
        modality,
        // La ruta decide QUÉ se le pide al cliente: el facematch solo una selfie, el KYC completo la
        // INE por ambos lados. Sin ella la pantalla no puede decirle al vendedor cuál toca.
        // `undefined` mientras la bifurcación no se resolvió — no se asume `FULL_KYC`, porque
        // todavía no se sabe.
        route:
            wire.customerRoute === 'FACEMATCH' || wire.customerRoute === 'FULL_KYC'
                ? wire.customerRoute
                : undefined,
        // La sesión no trae el contador; el motivo del rechazo viaja en `kyc/status`.
        retryCount: 0,
    };
}

/** `PaymentMethodEnum` del silo → el router del paso 04. `CASH_SALE` es contado (efectivo o tarjeta). */
function toPaymentMethodKind(method: string | null): WizardSession['paymentMethodKind'] {
    switch (method) {
        case 'CASH_SALE':
            return 'CASH_OR_CARD';
        case 'SUREKEEP':
            return 'SUREKEEP';
        case 'EXTERNAL':
            return 'EXTERNAL';
        default:
            return null;
    }
}

/** `SalePaymentMethodEnum` del silo (`WIRE`) → el vocabulario del cobro en pantalla (`SPEI`). */
export function toPaymentMethod(method: string): PaymentMethod {
    if (method === 'CARD') return 'CARD';
    if (method === 'WIRE' || method === 'SPEI') return 'SPEI';
    return 'CASH';
}

function toPayment(wire: WizardSessionWire): RegisteredPayment | undefined {
    if (!wire.payment) return undefined;
    return {
        method: wire.payment.method === null ? null : toPaymentMethod(wire.payment.method),
        amountCents: wire.payment.amountCents,
        payments: toPaymentLines(wire.payment.payments),
        // `detail` viaja como texto libre en el silo; el modelo de vista lo quiere desglosado. Sin
        // un contrato que lo estructure no se puede repartir en campos, así que queda vacío.
        detail: {},
    };
}

/**
 * Ficha comercial del equipo reservado. La compone el silo al reservar (S03) y la congela: acá solo
 * se copia, NUNCA se recalcula ni se completa con valores por defecto.
 *
 * Devolver `undefined` cuando el silo no manda `product` es deliberado: significa "no hay equipo
 * reservado" y el paso 04 lo dice con todas las letras. Rellenarlo con ceros pintaría un cobro de
 * $0 en el paso 07, que es peor que avisar.
 */
function toProduct(wire: WizardSessionWire): SelectedProduct | undefined {
    const p = wire.product;
    if (!p) return undefined;
    // Capacidad, color y gama vacíos se pintan como «sin dato»; el IMEI ausente se queda ausente.
    return {
        sku: p.sku,
        ...(p.imei ? { imei: p.imei } : {}),
        ...(p.lineId ? { lineId: p.lineId } : {}),
        cashPriceCents: p.cashPriceCents,
        brand: p.brand ?? '',
        model: p.model ?? '',
        capacity: p.capacity ?? '',
        color: p.color ?? '',
        imageUrl: p.imageUrl ?? null,
        tier: p.tier ?? '',
        eligibleFinanciers: p.eligibleFinanciers,
    };
}

const LINE_STATUSES = new Set<LineStatus>(['PENDING', 'RESERVED', 'RELEASING']);
const PRODUCT_CATEGORIES = new Set<ProductCategory>([
    'PHONE',
    'SIM',
    'MODEM',
    'ACCESSORY',
    'BUNDLE',
]);
const TRACKING_MODES = new Set<TrackingMode>(['IMEI', 'SERIAL', 'QUANTITY']);

/** Una categoría que el front no conoce se trata como accesorio: no pide identificadores. */
export function toProductCategory(value: string | null | undefined): ProductCategory {
    return PRODUCT_CATEGORIES.has(value as ProductCategory)
        ? (value as ProductCategory)
        : 'ACCESSORY';
}

/** Un modo de rastreo desconocido cae a cantidad; el silo rechaza el cierre si faltan piezas. */
export function toTrackingMode(value: string | null | undefined): TrackingMode {
    return TRACKING_MODES.has(value as TrackingMode) ? (value as TrackingMode) : 'QUANTITY';
}

/** Un estado desconocido se trata como PENDING: ni se cobra ni se puede soltar. */
function toLineStatus(value: string): LineStatus {
    return LINE_STATUSES.has(value as LineStatus) ? (value as LineStatus) : 'PENDING';
}

function toLinePaymentType(value: string | null): LinePaymentType | null {
    return value === 'CREDIT' || value === 'CASH' ? value : null;
}

/** Campos de Promociones de la línea; los ausentes o en `null` no se escriben. */
function toLinePromotionFields(l: ReservedLineWire): LinePromotionFields {
    return {
        ...(l.role === 'GIFT' && { role: l.role }),
        ...(l.promotionId != null && { promotionId: l.promotionId }),
        ...(l.promotionVersion != null && { promotionVersion: l.promotionVersion }),
        ...(l.linkedLineId != null && { linkedLineId: l.linkedLineId }),
        ...(l.discountCode != null && { discountCode: l.discountCode }),
        ...(l.discountCents != null && { discountCents: l.discountCents }),
        ...(l.originalPriceCents != null && { originalPriceCents: l.originalPriceCents }),
        ...(l.promotionDiscounts != null && { promotionDiscounts: l.promotionDiscounts }),
        ...(l.codeDiscountCents != null && { codeDiscountCents: l.codeDiscountCents }),
    };
}

/** Promo de venta de la sesión; sin id no hay promo, aunque venga la versión. */
function toSalePromotion(
    wire: WizardSessionWire,
): Pick<WizardSession, 'salePromotion' | 'salePromotions'> {
    const list = wire.salePromotions ?? [];
    if (wire.salePromotionId == null) {
        return list.length > 0 ? { salePromotions: list } : {};
    }
    const main = {
        promotionId: wire.salePromotionId,
        ...(wire.salePromotionVersion != null && {
            promotionVersion: wire.salePromotionVersion,
        }),
    };
    return { salePromotion: main, salePromotions: list.length > 0 ? list : [main] };
}

export function toLineProduct(p: LineProductWire | null | undefined): LineProduct | null {
    if (!p) return null;
    return {
        brand: p.brand ?? '',
        model: p.model ?? '',
        capacity: p.capacity ?? null,
        color: p.color ?? null,
        imageUrl: p.imageUrl ?? null,
        tier: p.tier ?? null,
        category: toProductCategory(p.category),
        trackingMode: toTrackingMode(p.trackingMode),
        ...(p.itemType && { itemType: p.itemType }),
        ...(p.description?.trim() ? { description: p.description } : {}),
    };
}

function toReservedLine(l: ReservedLineWire): ReservedLine {
    const components: ReservedComponent[] = (l.components ?? []).map((c) => ({
        sku: c.sku,
        qty: c.qty,
        category: toProductCategory(c.category),
        trackingMode: toTrackingMode(c.trackingMode),
    }));
    const identifiers: LineIdentifiers[] = (l.identifiers ?? []).map((i) => ({
        sku: i.sku,
        identifiers: [...i.identifiers],
    }));
    return {
        lineId: l.lineId,
        sku: l.sku,
        qty: l.qty,
        status: toLineStatus(l.status),
        expiresAt: l.expiresAt,
        basePriceCents: l.basePriceCents,
        eligibleFinanciers: l.eligibleFinanciers ?? [],
        financeable: l.financeable === true,
        paymentType: toLinePaymentType(l.paymentType),
        ...(l.financedCents != null && { financedCents: l.financedCents }),
        ...toLinePromotionFields(l),
        ...(l.promotionRevalidationPending === true && { promotionRevalidationPending: true }),
        sellItemCost: l.sellItemCost,
        mdmEnabled: l.mdmEnabled === true,
        mdmProvider: l.mdmProvider ?? null,
        components,
        identifiers,
        product: toLineProduct(l.product),
    };
}

/** Sesión anterior a las líneas: el silo manda `[]` pero trae el equipo viejo apartado por IMEI. */
function isPreLinesSession(wire: WizardSessionWire): boolean {
    if (wire.reservedLines == null) return true;
    return wire.reservedLines.length === 0 && Boolean(wire.reservedImei ?? wire.product?.imei);
}

/** `undefined` en la sesión anterior a las líneas; `[]` si las tuvo y ya no queda ninguna. */
function toReservedLines(wire: WizardSessionWire): ReservedLine[] | undefined {
    if (isPreLinesSession(wire)) return undefined;
    return (wire.reservedLines ?? []).map(toReservedLine);
}

function toWelcome(wire: WizardSessionWire): WelcomeState {
    const w = wire.welcome;
    return {
        addressConfirmed: w?.addressConfirmed ?? false,
        benefCaptured: w?.benefCaptured ?? false,
        annexSigned: w?.annexSigned ?? false,
        topupApplied: w?.topupApplied ?? false,
        level2: w?.accountLevel === 2,
        welcomeAccredited: w?.topupApplied ?? false,
    };
}

/**
 * Estado de entrega del paso 09. La instalación de la app no se declara: el paso la verifica contra
 * `…/app-onboarding`, así que aquí solo viaja lo que el silo publica en la fila de la saga.
 */
function toDelivery(wire: WizardSessionWire): DeliveryState {
    return {
        cardDelivered: wire.pcfProvisioned === true,
        // FAIL-SAFE hacia entregar, igual que el silo: darlo por hecho y equivocarse deja al cliente
        // sin tarjeta al salir del mostrador; equivocarse al revés cuesta un plástico, que se resuelve.
        cardAlreadyOwned: wire.hasPhysicalMxCard === true,
        pendingReasons: [],
    };
}

// `LINKED` es el comprador que ya traía cuenta PagoConFiado: no se le abre una nueva, se enlaza la
// que tiene. Omitirlo lo degradaba a `OPENING` y lo dejaba esperando una apertura que nunca ocurre.
const ACCOUNT_STATUSES = new Set<AccountStatus>(['OPENING', 'OPEN', 'FAILED', 'LINKED']);

/** Fuera de catálogo se descarta: un valor que ningún dropdown sabe pintar es peor que vacío. */
const MONTHLY_INCOMES = new Set<MonthlyIncome>(MONTHLY_INCOME_OPTIONS.map((o) => o.value));

const OCCUPATIONS = new Set<Occupation>([
    'EMPLOYED',
    'HOMEMAKER',
    'OTHER',
    'RETIRED',
    'SELF_EMPLOYED',
    'STUDENT',
    'UNEMPLOYED',
]);

const CLIENT_LEVELS = new Set(['NOT_ELIGIBLE', 'BRONZE', 'SILVER', 'GOLD']);

/** Bloque `loan` de la saga → contexto de crédito del front (level validado contra el enum). */
function toLoan(wire: WizardSessionWire): WizardSession['loan'] {
    if (!wire.loan) return undefined;
    const level =
        wire.loan.level && CLIENT_LEVELS.has(wire.loan.level)
            ? (wire.loan.level as NonNullable<WizardSession['loan']>['level'])
            : undefined;
    return {
        borrowerId: wire.loan.borrowerId,
        sciEvaluationId: wire.loan.sciEvaluationId,
        level,
        planId: wire.loan.planId,
        creditId: wire.loan.creditId,
    };
}

const BLOCKED_CATEGORIES = new Set<BlockedCategory>([
    'CUSTOMER_RECORD',
    'UNDER_REVIEW',
    'TEMPORARY',
]);

/**
 * El bloqueo de la venta → modelo de vista. Una razón desconocida se lee como `ACCOUNT_BLOCKED`, no
 * se descarta: descartarla dejaría pasar al catálogo una venta sin cuenta.
 */
function toBlocked(wire: WizardSessionWire): WizardBlocked | undefined {
    const blocked = wire.blocked;
    if (!blocked) return undefined;
    return {
        reason: blocked.reason === 'ACCOUNT_OPENING' ? 'ACCOUNT_OPENING' : 'ACCOUNT_BLOCKED',
        category: BLOCKED_CATEGORIES.has(blocked.category as BlockedCategory)
            ? (blocked.category as BlockedCategory)
            : null,
        since: blocked.since,
    };
}

const RESUME_REASON_SET = new Set<ResumeReason>(RESUME_REASONS);

/** Un `step` que no está en el catálogo del silo. `toStepId` lo degradaría al 01 en silencio. */
const WIRE_STEP = /^S(0[1-9]|1[01])$/;

/**
 * Destino de la reanudación. Un motivo o un paso que este front no conoce descarta el destino
 * entero; sin destino, el paso 01 cae al KYC como siempre.
 */
function toResumeTarget(wire: WizardSessionWire): ResumeTarget | undefined {
    const target = wire.resumeTarget;
    if (!target || !WIRE_STEP.test(target.step)) return undefined;
    if (!RESUME_REASON_SET.has(target.reason as ResumeReason)) return undefined;
    return { step: toStepId(target.step), reason: target.reason as ResumeReason };
}

/** Un slug que este front no conoce se descarta: navegar a él deja una pantalla en blanco. */
function toResumeSlug(slug: string | null | undefined): WizardStepSlug | null {
    return slug && RESUME_SLUGS.includes(slug as WizardStepSlug) ? (slug as WizardStepSlug) : null;
}

function toFinancingPreview(
    preview: WizardSessionWire['financingPreview'],
): FinancingPreview | undefined {
    if (preview == null) return undefined;
    return {
        capCents: preview.capCents,
        lines: preview.lines.map((l) => ({ lineId: l.lineId, financedCents: l.financedCents })),
    };
}

const EMAIL_STATES = new Set<EmailState>(['EXISTING', 'CAPTURED', 'PENDING']);
const EMAIL_KINDS = new Set<EmailKind>(['PERSONAL', 'CORPORATE']);

/** Correo de la venta; el completo solo si lo capturó el vendedor. Estado desconocido se descarta. */
function toSessionEmail(wire: WizardSessionWire): SessionEmail | undefined {
    const state = wire.emailState;
    if (!state || !EMAIL_STATES.has(state)) return undefined;
    const kind = wire.emailType && EMAIL_KINDS.has(wire.emailType) ? wire.emailType : undefined;
    return {
        state,
        ...(kind ? { kind } : {}),
        ...(wire.emailMasked ? { masked: wire.emailMasked } : {}),
        ...(state === 'CAPTURED' && wire.email ? { address: wire.email } : {}),
    };
}

const ACCOUNT_OPENING_CANCEL_REASONS = new Set<string>([
    SystemCancelReasonEnum.ALREADY_HAS_ACCOUNT,
    SystemCancelReasonEnum.CURP_OF_OTHER_CUSTOMER,
]);

/** Motivo de cancelación propio de la apertura; cualquier otro no se copia. */
function toAccountOpeningCancelReason(
    reason: string | null | undefined,
): Pick<WizardSession, 'accountOpeningCancelReason'> {
    return reason && ACCOUNT_OPENING_CANCEL_REASONS.has(reason)
        ? { accountOpeningCancelReason: reason as AccountOpeningCancelReason }
        : {};
}

/** Fila plana de la saga → modelo que consumen las pantallas del wizard. */
export function toWizardSession(wire: WizardSessionWire): WizardSession {
    const occupation =
        wire.occupation && OCCUPATIONS.has(wire.occupation as Occupation)
            ? (wire.occupation as Occupation)
            : undefined;
    const creditPromotionBonus = toCreditPromotionBonus(wire.creditPromotionBonus);
    const email = toSessionEmail(wire);

    return {
        sessionId: wire.sessionId,
        state: toState(wire.state),
        expired: wire.state === 'EXPIRED',
        quoteDiscarded: wire.cancelReason === SystemCancelReasonEnum.SWEEP_CREDIT_DISCARDED,
        takenOverByOtherStore:
            wire.cancelReason === SystemCancelReasonEnum.TAKEN_OVER_BY_OTHER_STORE,
        ...(wire.accountOpening === true && { accountOpening: true }),
        ...toAccountOpeningCancelReason(wire.cancelReason),
        currentStep: toStepId(wire.currentStep),
        paymentMethodKind: toPaymentMethodKind(wire.paymentMethod),
        externalProvider: wire.externalProvider ?? undefined,
        loan: toLoan(wire),
        phone: wire.phone,
        phoneStatus: toPhoneStatus(wire.fiadoPhoneStatus),
        // `firstName` solo viaja en la respuesta de `POST /wizard/sessions` (saludo "Cliente
        // reconocido"); la fila de la saga no lo guarda.
        otpVerified: wire.otpVerified,
        otpSkipped: wire.otpSkipped,
        resumeOtpRequired: wire.resumeOtpRequired === true,
        resumeTarget: toResumeTarget(wire),
        resumeSlug: toResumeSlug(wire.resumeSlug),
        ...(wire.cashOnly === true && { cashOnly: true }),
        consentsAccepted: wire.consents != null,
        ...(email ? { email } : {}),
        kyc: toKyc(wire),
        occupation: occupation
            ? { code: occupation, otherText: wire.occupationOther ?? undefined }
            : undefined,
        monthlyIncome: MONTHLY_INCOMES.has(wire.monthlyIncome as MonthlyIncome)
            ? (wire.monthlyIncome as MonthlyIncome)
            : undefined,
        accountStatus: ACCOUNT_STATUSES.has(wire.accountStatus as AccountStatus)
            ? (wire.accountStatus as AccountStatus)
            : 'OPENING',
        blocked: toBlocked(wire),
        product: toProduct(wire),
        reservedLines: toReservedLines(wire),
        financedLineId: wire.financedLineId ?? undefined,
        ...toSalePromotion(wire),
        financingPreview: toFinancingPreview(wire.financingPreview),
        payment: toPayment(wire),
        folio: wire.folio ?? undefined,
        saleId: wire.saleId ?? undefined,
        piecesHeldAt: wire.piecesHeldAt ?? undefined,
        delivery: toDelivery(wire),
        welcome: toWelcome(wire),
        ...(creditPromotionBonus && { creditPromotionBonus }),
        storeId: wire.storeId,
        sellerId: wire.sellerRetailUserId,
        reservationExpiresAt: wire.reservationExpiresAt ?? undefined,
        updatedAt:
            typeof wire.updatedAt === 'number'
                ? new Date(wire.updatedAt).toISOString()
                : wire.updatedAt,
    };
}

// ── GET /wizard/sessions?phone= ──────────────────────────────────────────────

/** Espejo de `RetailWizard.ActiveSessionByPhoneResponse`: los tres campos llegan en `null` sin sesión. */
export interface ActiveSessionWire {
    found: boolean;
    sessionId: string | null;
    currentStep: string | null;
    state: string | null;
    /** El bloqueo, sin `code` ni `detail`: la ficha solo pinta la etiqueta y el «hace N días». */
    blocked?: { category: string | null; since: string } | null;
    resumeSlug?: string | null;
    resumeTargetStep?: string | null;
    exactUntil?: string | null;
    moneyAtStake?: boolean;
    moneyAtStakeKind?: components['schemas']['ActiveSessionByPhoneResumeFields']['moneyAtStakeKind'];
    cashOnly?: boolean;
}

const MONEY_AT_STAKE_KINDS: ReadonlySet<string> = new Set(Object.values(MoneyAtStakeKindEnum));

/** Un tipo fuera del catálogo se lee como `null`: la ficha no afirma un dinero que no conoce. */
function toMoneyAtStakeKind(kind: string | null | undefined): MoneyAtStakeKind | null {
    return kind && MONEY_AT_STAKE_KINDS.has(kind) ? (kind as MoneyAtStakeKind) : null;
}

/**
 * Sesión activa del teléfono → modelo de vista.
 *
 * Un `currentStep` fuera del catálogo se descarta en vez de pasar por `toStepId`: ese degrada al 01
 * y la pestaña afirmaría un paso que la venta nunca vio.
 */
export function toActiveSession(wire: ActiveSessionWire): ActiveWizardSession {
    if (!wire.found) return { found: false, sessionId: null, currentStep: null, state: null };
    const blocked = wire.blocked;
    return {
        found: true,
        sessionId: wire.sessionId,
        currentStep:
            wire.currentStep && WIRE_STEP.test(wire.currentStep)
                ? toStepId(wire.currentStep)
                : null,
        state: wire.state ? toState(wire.state) : null,
        blocked: blocked
            ? {
                  category: BLOCKED_CATEGORIES.has(blocked.category as BlockedCategory)
                      ? (blocked.category as BlockedCategory)
                      : null,
                  since: blocked.since,
              }
            : undefined,
        resumeSlug: toResumeSlug(wire.resumeSlug),
        resumeTargetStep:
            wire.resumeTargetStep && WIRE_STEP.test(wire.resumeTargetStep)
                ? toStepId(wire.resumeTargetStep)
                : null,
        exactUntil: wire.exactUntil,
        moneyAtStake: wire.moneyAtStake === true,
        moneyAtStakeKind: toMoneyAtStakeKind(wire.moneyAtStakeKind),
        cashOnly: wire.cashOnly === true,
    };
}

// ── POST /prospects/{id}/resume ──────────────────────────────────────────────

const ENTRY_MODES: ReadonlySet<string> = new Set([
    ProspectResumeModeEnum.EXACT,
    ProspectResumeModeEnum.CHECKPOINT,
]);

/** Sin sesión es venta nueva; con sesión y un modo desconocido entra sin prometer la pantalla. */
export function toProspectResume(wire: ProspectResumeResponse): ProspectResume {
    if (!wire.sessionId) return { sessionId: null, mode: 'NEW' };
    return {
        sessionId: wire.sessionId,
        mode: ENTRY_MODES.has(wire.mode) ? wire.mode : 'CHECKPOINT',
    };
}

// ── GET /wizard/sessions/{id}/kyc/status ─────────────────────────────────────

/**
 * Shape REAL del cable de `kyc/status`. NO es `KycState`: el silo devuelve
 * `{ kycStatus, accountStatus, subChecks }` (ver `WizardSessionManager.kycStatus`), mientras que
 * `KycState` es el tipo de DOMINIO que arma este front.
 *
 * 🔴 Existe porque el client hacía `call<KycState>(...)` — un cast a ciegas sobre una respuesta que
 * no tiene esa forma. `tsc` quedaba verde y el tipo prometía `status`, `modality` y `retryCount` que
 * el cable nunca manda: leer cualquiera de los tres daba `undefined` en Dev y el valor correcto en el
 * mock. Mismo patrón que `WizardSessionWire`, por el mismo motivo.
 */
export interface KycStatusWire {
    /** PENDING | VERIFIED | FAILED | MANUAL | REVIEW (RetailKycStatusEnum). */
    kycStatus: string | null;
    accountStatus: string | null;
    /** Avance estación por estación. `null` mientras no cerró ninguna. */
    subChecks: KycSubChecks | null;
    /**
     * Lo que el proveedor extrajo del INE + el estado de la cuenta PagoConFiado. El silo lo lee del
     * customer (su dueño) al responder el poll y no lo persiste: es PII y el wizard es PII-zero.
     *
     * `null` mientras el KYC no aprueba — y también si el silo no pudo leer el customer, porque el
     * resumen es complementario y jamás frena la pantalla.
     */
    verified: KycVerifiedWire | null;
    /** Cómo cerró el comprobante de domicilio. `null` mientras no cerró ninguno. */
    proofOfAddress?: ProofOfAddressWire | null;
    /**
     * ¿El silo va a exigir domicilio al guardar el perfil? Es el predicado de `assertAddressSettled`,
     * publicado para que la pantalla gatee con la MISMA pregunta. Ausente en despliegues previos.
     */
    addressRequired?: boolean;
    /** Motivo por el que la verificación no avanzó. Ausente en despliegues previos. */
    kycFailure?: { code: string } | null;
    /** Intento vigente por flujo (`RetailWizard.KycStatusProgress`). Ausente en despliegues previos. */
    progress?: KycProgressWire | null;
}

/** Resultado del comprobante de domicilio tal como viaja por el cable. */
export interface ProofOfAddressWire {
    /** `ProofAddressStatusEnum` del type-kit — 12 valores. */
    status: string;
    addressPersisted: boolean;
    verificationId: string;
    /**
     * ¿El comprobante ya cerró? Lo deriva el silo de su set de status terminales. Opcional: los
     * despliegues previos a que el campo existiera responden sin él.
     */
    terminal?: boolean;
}

/** Intento de un flujo tal como viaja: el cable no es de fiar, se valida al traducir. */
export interface KycAttemptProgressWire {
    attemptId: string;
    modality: string;
    link?: string | null;
    stage: string;
    stageAt: string;
    attemptStartedAt?: string;
}

export interface KycProgressWire {
    identity: KycAttemptProgressWire | null;
    facematch: KycAttemptProgressWire | null;
    proofOfAddress: KycAttemptProgressWire | null;
}

/** Identidad verificada tal como la publica `GET /kyc/status`. */
export interface KycVerifiedWire {
    fullName: string;
    curp: string | null;
    birthDate: string | null;
    /** Vigencia del INE con el que se verificó; `null` si el OCR no la leyó. */
    documentExpiresAt: string | null;
    /** `national-id`, `passport`, … — el catálogo crudo del proveedor. */
    documentType?: string | null;
    /** Domicilio del INE en UNA línea, ya compuesto por el silo. `null` si el OCR no extrajo ninguno. */
    addressText: string | null;
    addressConfirmed: boolean;
}

/**
 * Traduce la respuesta de `kyc/status` al dominio. La modalidad NO viaja en este endpoint, así que el
 * estado se deriva sin ella: un `PENDING` sin modalidad conocida se lee como `WAITING`, que es el
 * único momento en que este endpoint se consulta con el KYC en curso.
 */
export function toKycStatusResponse(wire: KycStatusWire): Pick<
    KycState,
    'status' | 'subChecks' | 'verified' | 'proofOfAddress' | 'addressRequired' | 'failure'
> & {
    accountStatus: string | null;
    progress: KycProgress;
} {
    return {
        status: toKycStatus(wire.kycStatus, 'STORE_DEVICE'),
        subChecks: wire.subChecks ?? null,
        accountStatus: wire.accountStatus,
        verified: toKycVerified(wire.verified),
        proofOfAddress: toProofOfAddress(wire.proofOfAddress),
        // Ausente se deja ausente: quien lee distingue "el silo no lo dijo" de "dijo que no".
        addressRequired: wire.addressRequired,
        failure: toKycFailure(wire.kycFailure),
        progress: toKycProgress(wire.progress),
    };
}

const KYC_FAILURE_CODE_SET = new Set<string>(KYC_FAILURE_CODES);
/** Fallas del alta que el silo reintenta solo en el siguiente poll. */
const TRANSIENT_REGISTRATION_CODES = new Set(['UNEXPECTED_ERROR', 'INTERNAL_ERROR']);

/** `kycFailure.code` del cable → motivo del dominio; un code desconocido cae al genérico. */
function toKycFailure(wire?: { code: string } | null): KycFailure | null {
    if (!wire?.code) return null;
    if (KYC_FAILURE_CODE_SET.has(wire.code)) return wire.code as KycFailure;
    if (TRANSIENT_REGISTRATION_CODES.has(wire.code)) return 'REGISTRATION_PENDING';
    return 'UNKNOWN';
}

const PROOF_ADDRESS_STATUS_SET = new Set<string>(PROOF_ADDRESS_STATUSES);

/**
 * Resultado del comprobante → dominio. Un estado fuera del catálogo se marca `UNKNOWN` en vez de
 * descartarse: descartarlo devolvería `null`, que la pantalla lee como "todavía no cerró" — o sea,
 * como que la venta puede seguir esperando algo que ya pasó.
 */
function toProofOfAddress(wire?: ProofOfAddressWire | null): ProofOfAddressResult | null {
    if (!wire) return null;
    return {
        status: PROOF_ADDRESS_STATUS_SET.has(wire.status)
            ? (wire.status as ProofAddressStatus)
            : 'UNKNOWN',
        addressPersisted: wire.addressPersisted,
        verificationId: wire.verificationId,
        // Ausente se deja ausente: quien lee distingue "el silo no lo dijo" de "dijo que no".
        terminal: wire.terminal,
    };
}

const ATTEMPT_STAGES = new Set<string>(Object.values(KycAttemptStageEnum));

/** Un intento con etapa o modalidad desconocida se descarta: el flujo queda sin intento. */
function toAttemptProgress(wire?: KycAttemptProgressWire | null): KycAttemptProgress | null {
    if (!wire?.attemptId || !ATTEMPT_STAGES.has(wire.stage)) return null;
    if (wire.modality !== 'STORE_DEVICE' && wire.modality !== 'CUSTOMER_LINK') return null;
    return {
        attemptId: wire.attemptId,
        modality: wire.modality,
        link: wire.link ?? null,
        stage: wire.stage as KycAttemptStageEnum,
        stageAt: wire.stageAt,
        // El contrato lo exige; si un silo viejo no lo manda, el tope cuenta desde la etapa.
        attemptStartedAt: wire.attemptStartedAt ?? wire.stageAt,
    };
}

/** `progress` del cable → dominio. Sin `progress` (wizard previo) los tres flujos van en `null`. */
export function toKycProgress(wire?: KycProgressWire | null): KycProgress {
    return {
        identity: toAttemptProgress(wire?.identity),
        facematch: toAttemptProgress(wire?.facematch),
        proofOfAddress: toAttemptProgress(wire?.proofOfAddress),
    };
}

/**
 * Respuesta de `GET …/account-status` tal como viaja por el cable.
 *
 * El desglose del expediente (`verdict`, `missingSteps`, `steps`) sigue viajando y no se declara:
 * sus únicas consumidoras eran las pantallas de etapas, y la espera del paso 02 ya no las tiene.
 */
export interface AccountStatusWire {
    accountReady: boolean;
    outcome: string;
}

const ACCOUNT_OUTCOMES = new Set<AccountOutcome>([
    'READY',
    'IN_PROGRESS',
    'MISSING_INFO',
    'CANNOT_OPEN',
]);

/**
 * `account-status` del cable → el dominio. El desenlace se valida en vez de castearse: un valor
 * desconocido cae al estado que NO decide nada, para no bloquear ni dejar pasar de más.
 */
export function toAccountStatus(wire: AccountStatusWire): AccountStatusResult {
    const outcome = wire.outcome as AccountOutcome;
    return {
        accountReady: wire.accountReady,
        outcome: ACCOUNT_OUTCOMES.has(outcome) ? outcome : 'IN_PROGRESS',
    };
}

/**
 * `verified` del cable → el dominio. Regla de este archivo: acá SOLO se traduce, un dato ausente se
 * deja ausente y jamás se inventa. Por eso los nulos del cable pasan a `undefined` en vez de a
 * cadenas vacías o guiones: quien pinta decide cómo se ve un dato que no vino, no el mapper.
 */
function toKycVerified(wire: KycVerifiedWire | null): KycState['verified'] {
    if (!wire) return undefined;
    return {
        fullName: wire.fullName,
        curp: wire.curp ?? undefined,
        birthDate: wire.birthDate ?? undefined,
        documentExpiresAt: wire.documentExpiresAt ?? undefined,
        documentType: wire.documentType ?? undefined,
        addressText: wire.addressText ?? undefined,
        addressConfirmed: wire.addressConfirmed,
    };
}

// ── GET /wizard/sessions/{id}/app-onboarding ─────────────────────────────────

/** Las 4 condiciones del registro en la app tal como viajan por el cable. */
export interface AppOnboardingWire {
    steps: AppOnboardingStepWire[] | null;
    /**
     * Ninguna condición quedó pendiente. Lo decide el silo — `UNVERIFIABLE` no bloquea. Opcional:
     * ausente NO es lo mismo que asentado, y es lo que responde un silo que todavía no lo publica.
     */
    allSettled?: boolean | null;
    /** La tienda tiene tarjetas físicas; ausente si el silo no pudo leer el inventario. */
    cardStockAvailable?: boolean | null;
}

export interface AppOnboardingStepWire {
    /** `PASSWORD`, `CONTRACT_SIGNED`, … Abierto a propósito: no se valida contra una lista. */
    step: string;
    /** COMPLETED | PENDING | UNVERIFIABLE. */
    status: string;
    reason?: string | null;
}

const APP_ONBOARDING_STATUSES = new Set<AppOnboardingStatus>([
    'COMPLETED',
    'PENDING',
    'UNVERIFIABLE',
]);

/**
 * Condiciones del registro en la app → dominio. El paso 09 pinta tres estados y solo `PENDING`
 * significa "el cliente no lo ha hecho", así que un status fuera del catálogo cae a `UNVERIFIABLE`:
 * en pendiente el vendedor esperaría a alguien que ya cumplió.
 *
 * La condición desconocida se conserva con su clave — descartarla escondería algo que el silo sí
 * está contando en `allSettled`.
 */
export function toAppOnboarding(wire: AppOnboardingWire): AppOnboardingState {
    return {
        steps: (wire.steps ?? []).map(toAppOnboardingStep),
        // Solo un `true` explícito habilita el paso siguiente: lo que se juega es entregar el equipo.
        allSettled: wire.allSettled === true,
        cardStockAvailable:
            typeof wire.cardStockAvailable === 'boolean' ? wire.cardStockAvailable : null,
    };
}

function toAppOnboardingStep(wire: AppOnboardingStepWire): AppOnboardingStepState {
    const status = wire.status as AppOnboardingStatus;
    return {
        step: wire.step,
        status: APP_ONBOARDING_STATUSES.has(status) ? status : 'UNVERIFIABLE',
        reason: wire.reason ?? undefined,
    };
}

// ── Enrolamiento del candado (paso 09, crédito) ───────────────────────────────
export interface DeviceEnrollmentWire {
    enrollment?: {
        status?: string | null;
        imei?: string | null;
        failureReason?: string | null;
        failureCode?: string | null;
        retryable?: boolean | null;
    } | null;
    lockAppUrl?: string | null;
    mdmProvider?: string | null;
    provisioningType?: string | null;
    creditStatus?: string | null;
    checklist?: ActivationChecklist | null;
    missingItems?: string[] | null;
    promotionBonus?: { status?: string | null; amountCents?: number | null } | null;
    creditPromotionBonus?: WizardSessionWire['creditPromotionBonus'];
    /** Venta que cerró el servidor al ver el crédito ACTIVE con las piezas fijadas. */
    saleId?: string | null;
}

/** Un desenlace que este front no conoce NO celebra: sin certeza de que hubo pago, no hay confeti. */
const PROMOTION_BONUS_STATUSES = new Set<PromotionBonusStatus>(['SETTLED', 'FAILED', 'UNVERIFIED']);

const CREDIT_PROMOTION_BONUS_STATUSES = new Set<CreditPromotionBonusStatus>([
    'RESERVED',
    'SETTLED',
    'FAILED',
    'UNVERIFIED',
]);

/** Solo con promo, monto real y un estado conocido; lo demás no se pinta. */
function toCreditPromotionBonus(
    wire: WizardSessionWire['creditPromotionBonus'],
): CreditPromotionBonusState | undefined {
    const status = wire?.status as CreditPromotionBonusStatus | undefined;
    const amountCents = wire?.amountCents;
    if (!wire?.promotionId || !status || !CREDIT_PROMOTION_BONUS_STATUSES.has(status))
        return undefined;
    if (typeof amountCents !== 'number' || !Number.isFinite(amountCents) || amountCents <= 0)
        return undefined;
    return {
        promotionId: wire.promotionId,
        amountCents,
        status,
        ...(wire.pendingSince && { pendingSince: wire.pendingSince }),
    };
}

const MDM_PROVIDERS = new Set<string>(Object.values(MdmProviderEnum));

/**
 * El proveedor solo se lee cuando es uno que este front conoce. Un valor desconocido se deja
 * ausente: el paso 09 cae al flujo con instructivo, que es el que no le quita pasos a nadie.
 */
export function toMdmProvider(value: string | null | undefined): MdmProviderEnum | undefined {
    return value && MDM_PROVIDERS.has(value) ? (value as MdmProviderEnum) : undefined;
}

const PROVISIONING_TYPES = new Set<string>([
    DeviceProvisioningTypeEnum.KNOX_GUARD,
    DeviceProvisioningTypeEnum.PAY_TRIGGER,
]);

/** Solo un método conocido pasa; cualquier otro queda ausente y la pantalla usa la guía genérica. */
export function toProvisioningType(
    value: string | null | undefined,
): DeviceProvisioningTypeEnum | undefined {
    return value && PROVISIONING_TYPES.has(value)
        ? (value as DeviceProvisioningTypeEnum)
        : undefined;
}

const DEVICE_ENROLLMENT_STATUSES = new Set<DeviceEnrollmentStatus>([
    'PENDING',
    'ENROLLED',
    'PROVIDER_UNREACHABLE',
    'FAILED',
]);

/**
 * Un status que este front no conoce cae en `PROVIDER_UNREACHABLE`, nunca en `PENDING`: leerlo como
 * "sigue esperando" le pediría al vendedor que espere algo que ya no va a llegar.
 */
export function toDeviceEnrollment(wire: DeviceEnrollmentWire): DeviceEnrollmentResult {
    const status = wire.enrollment?.status as DeviceEnrollmentStatus | undefined;
    const creditPromotionBonus = toCreditPromotionBonus(wire.creditPromotionBonus);
    return {
        // Sin bloque `enrollment` no hay expediente que mostrar — no se fabrica uno vacío.
        enrollment: wire.enrollment
            ? {
                  status:
                      status && DEVICE_ENROLLMENT_STATUSES.has(status)
                          ? status
                          : 'PROVIDER_UNREACHABLE',
                  imei: wire.enrollment.imei ?? '',
                  ...toFailureDetail(wire.enrollment),
              }
            : null,
        lockAppUrl: wire.lockAppUrl ?? '',
        mdmProvider: toMdmProvider(wire.mdmProvider),
        provisioningType: toProvisioningType(wire.provisioningType),
        creditStatus: wire.creditStatus ?? undefined,
        checklist: wire.checklist ?? undefined,
        missingItems: wire.missingItems ?? undefined,
        promotionBonus: toPromotionBonus(wire.promotionBonus),
        ...(creditPromotionBonus && { creditPromotionBonus }),
        saleId: wire.saleId ?? undefined,
    };
}

const FAILURE_REASONS = new Set<DeviceEnrollmentFailureReason>([
    'MODEL_NOT_SUPPORTED',
    'ALREADY_IN_PROVIDER',
    'PROVIDER_REJECTED',
]);

const FAILURE_CODES = new Set<DeviceEnrollmentFailureCode>([
    'INVALID_IMEI',
    'MDM_DEVICE_REGISTERED_ELSEWHERE',
    'MDM_DEVICE_MODEL_NOT_SUPPORTED',
    'MDM_PROVIDER_CONFIG_ERROR',
    'MDM_ENROLL_REJECTED',
]);

/** Motivo, code y reintento del fallo; uno desconocido se omite y la pantalla cae al genérico. */
function toFailureDetail(
    wire: NonNullable<DeviceEnrollmentWire['enrollment']>,
): Pick<DeviceEnrollment, 'failureReason' | 'failureCode' | 'retryable'> {
    const reason = wire.failureReason as DeviceEnrollmentFailureReason | null | undefined;
    const code = wire.failureCode as DeviceEnrollmentFailureCode | null | undefined;
    return {
        ...(reason && FAILURE_REASONS.has(reason) && { failureReason: reason }),
        ...(code && FAILURE_CODES.has(code) && { failureCode: code }),
        ...(typeof wire.retryable === 'boolean' && { retryable: wire.retryable }),
    };
}

/** El bono solo se pinta con un estado conocido y un monto real; cualquier otra cosa se descarta. */
function toPromotionBonus(
    wire: DeviceEnrollmentWire['promotionBonus'],
): PromotionBonusOutcome | undefined {
    const status = wire?.status as PromotionBonusStatus | undefined;
    const amountCents = wire?.amountCents;
    if (!status || !PROMOTION_BONUS_STATUSES.has(status)) return undefined;
    if (typeof amountCents !== 'number' || !Number.isFinite(amountCents) || amountCents <= 0) {
        return undefined;
    }
    return { status, amountCents };
}
